import React from "react";
import { Link } from "react-router-dom";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import { useLanguage } from "../../../LanguageContext"; // Adjust path as needed

const ContactUserTable = ({
  clients,
  selectedClients,
  onSelectAll,
  onSelectClient,
  onDeleteClient,
  onRenewSubscription,
  allSelected,
}) => {
  const { language } = useLanguage();

  const permissions = JSON.parse(localStorage.getItem("permissions"));

  const updateTrainserExists = permissions.some(
    (permission) => permission.name === "update_trainer"
  );
  const deleteTrainserExists = permissions.some(
    (permission) => permission.name === "delete_trainer"
  );
  const isArabic = language === "ar";

  const translations = {
    name: isArabic ? "الاسم" : "Name",
    phone: isArabic ? "الهاتف" : "Phone",
    email: isArabic ? "الإيميل" : "Email",
    message: isArabic ? "الرسالة" : "Message",
    address: isArabic ? "العنوان" : "Address",
    birthDate: isArabic ? "تاريخ الميلاد" : "BirthDate",
    actions: isArabic ? "الإجراءات" : "Actions",
    selectAll: isArabic ? "تحديد الكل" : "Select All",
    edit: isArabic ? "تعديل" : "Edit",
    renew: isArabic ? "تجديد" : "Renew",
    delete: isArabic ? "حذف" : "Delete",
    age: isArabic ? "العمر" : "Age",
    editTrainer: isArabic ? "تعديل المدرب" : "Edit Trainer",
    renewSubscription: isArabic ? "تجديد الاشتراك" : "Renew Subscription",
    deleteTrainer: isArabic ? "حذف المدرب" : "Delete Trainer",
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    switch (status) {
      case "active":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "Ending Soon":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case "inactive":
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(
      isArabic ? "ar-SA" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    );
  };

  return (
    <div className="overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className={`px-6 py-3 text-${
                  isArabic ? "right" : "left"
                } text-xs font-medium text-gray-500 uppercase tracking-wider`}
              >
                {translations.name}
              </th>
              <th
                className={`px-6 py-3 text-${
                  isArabic ? "right" : "left"
                } text-xs font-medium text-gray-500 uppercase tracking-wider`}
              >
                {translations.phone}
              </th>
              <th
                className={`px-6 py-3 text-${
                  isArabic ? "right" : "left"
                } text-xs font-medium text-gray-500 uppercase tracking-wider`}
              >
                {translations.email}
              </th>
              <th
                className={`px-6 py-3 text-${
                  isArabic ? "right" : "left"
                } text-xs font-medium text-gray-500 uppercase tracking-wider`}
              >
                {translations.message}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clients.map((client) => (
              <tr key={client.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className={isArabic ? "mr-4" : "ml-4"}>
                      <div className="text-sm font-medium text-gray-900">
                        {client.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-${
                    isArabic ? "right" : "left"
                  }`}
                >
                  {client.phone}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-${
                    isArabic ? "right" : "left"
                  }`}
                >
                  {client.email}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-${
                    isArabic ? "right" : "left"
                  }`}
                >
                  {client?.message}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden">
        <div className="p-4 bg-gray-50 border-b">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => onSelectAll(e.target.checked)}
              className={`h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded ${
                isArabic ? "ml-2" : "mr-2"
              }`}
            />
            <span className="text-sm font-medium text-gray-700">
              {translations.selectAll}
            </span>
          </label>
        </div>

        <div className="divide-y divide-gray-200">
          {clients.map((client) => (
            <div key={client.id} className="p-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={(e) => onSelectClient(client.id, e.target.checked)}
                  className={`h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mt-1 ${
                    isArabic ? "ml-2" : "mr-2"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {client.name}
                    </h3>
                    <span className={getStatusBadge(client.subscriptionStatus)}>
                      {client.subscriptionStatus}
                    </span>
                  </div>
                  <div className="mt-1 space-y-1">
                    <p
                      className={`text-sm text-gray-500 text-${
                        isArabic ? "right" : "left"
                      }`}
                    >
                      {client.email}
                    </p>
                    <p
                      className={`text-sm text-gray-500 text-${
                        isArabic ? "right" : "left"
                      }`}
                    >
                      {client.phone}
                    </p>
                    <p
                      className={`text-sm text-gray-500 text-${
                        isArabic ? "right" : "left"
                      }`}
                    >
                      {translations.age}: {client.age}
                    </p>
                  </div>
                  <div
                    className={`mt-3 flex items-center ${
                      isArabic ? "space-x-reverse" : ""
                    } space-x-4`}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContactUserTable;
